import { ReactElement, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Text } from '@hopskipdrive/react-components';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';

import { useToast } from '../../shared/contexts/ToastContext';
import FilterPanel from '../features/FilterPanel';
import AddIncidentForm from '../features/IncidentForm/AddIncidentForm';
import EditIncidentForm from '../features/IncidentForm/EditIncidentForm';
import IncidentTable from '../features/IncidentTable';
import Pagination from '../features/IncidentTable/Pagination';
import {
  useDeleteIncident,
  useGetAllSafetyIncidents,
  usePatchIncident,
  usePostIncident,
} from '../hooks/useIncidentApi';

import { PageContent, PageHeading } from './SafetyIncidents.styles';

const SafetyIncidents = (): ReactElement => {
  const incidentsPerPage = 10;
  const [openAdd, setOpenAdd] = useState<boolean>(false);
  const [activeRowId, setActiveRowId] = useState<number>(-1);
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState({});
  const offenderTypeString = searchParams.get('offenderType') || null;
  const offenderType = offenderTypeString ? JSON.parse(decodeURIComponent(offenderTypeString)) : null;
  const accountsString = searchParams.get('accountIds') || null;
  const selectedAccounts = accountsString ? JSON.parse(decodeURIComponent(accountsString)) : [];
  const ridersString = searchParams.get('riderIds') || null;
  const selectedRiders = ridersString ? JSON.parse(decodeURIComponent(ridersString)) : [];
  const driversString = searchParams.get('driverIds') || null;
  const selectedDrivers = driversString ? JSON.parse(decodeURIComponent(driversString)) : [];

  const { addToast } = useToast();

  const { isFetching, isError: isIncidentError, data: incidentFetchResponse } = useGetAllSafetyIncidents(query);

  const onApiSuccess = () => {
    setActiveRowId(-1);
    setOpenAdd(false);
  };

  const onApiError = (error) => {
    error?.errors.map((item) => {
      addToast({ severity: 'error', message: item });
    });
  };

  const { mutate: sendAdd, isPending: sendAddPending } = usePostIncident(onApiSuccess, onApiError);
  const { mutate: sendEdit, isPending: sendEditPending } = usePatchIncident(onApiSuccess, onApiError);
  const { mutate: sendDelete, isPending: sendDeletePending } = useDeleteIncident(() => {
    onApiSuccess();
    addToast({ severity: 'success', message: 'Incident deleted' });
  }, onApiError);

  useEffect(() => {
    setQuery({
      page: searchParams.get('page') || '1',
      perPage: incidentsPerPage,
      sortDir: searchParams.get('sortDir') || 'desc',
      sortField: searchParams.get('sortField') || 'id',
      accountIds: searchParams.get('accountIds'),
      driverIds: searchParams.get('driverIds'),
      incidentStartDate: searchParams.get('startDate'),
      incidentEndDate: searchParams.get('endDate'),
      classificationId: searchParams.get('classification'),
      subclassificationId: searchParams.get('subclassification'),
      priority: searchParams.get('priority'),
      riderIds: searchParams.get('riderIds'),
      status: searchParams.get('status'),
      ...(offenderType && { offenderType: offenderType.value }),
      ...(selectedAccounts.length > 0 && { accountIds: selectedAccounts.map((account) => account.value) }),
      ...(selectedRiders.length > 0 && { riderIds: selectedRiders.map((rider) => rider.value) }),
      ...(selectedDrivers.length > 0 && { driverIds: selectedDrivers.map((driver) => driver.value) }),
    });
  }, [searchParams]);

  return (
    <>
      <PageHeading>
        <Text variant="h6">Trust and Safety Incidents</Text>
        <Button color="primary" onClick={() => setOpenAdd(true)}>
          <AddIcon />
          Add Incident
        </Button>
      </PageHeading>
      <PageContent>
        <FilterPanel />
        <IncidentTable
          isFetching={isFetching}
          isError={isIncidentError}
          incidents={incidentFetchResponse?.data || []}
          activeRow={activeRowId}
          setActiveRow={setActiveRowId}
        />
        <Pagination
          currentPage={parseInt(searchParams.get('page') || '1')}
          pageCount={Math.ceil(incidentFetchResponse?.meta?.totalCount / incidentsPerPage)}
          changePage={(page) => {
            const newParams = new URLSearchParams(searchParams);

            newParams.set('page', page.toString());

            setSearchParams(newParams);
          }}
        />
      </PageContent>

      <AddIncidentForm
        isOpen={openAdd}
        isSubmitting={sendAddPending}
        onClose={() => {
          setOpenAdd(false);
        }}
        onSubmit={(data) => {
          const formattedData = {
            ...data,
            incidentDate: data.incidentDate ? format(new Date(data.incidentDate), 'yyyy-MM-dd') : null,
            reportedOn: data.reportedOn ? format(new Date(data.reportedOn), 'yyyy-MM-dd') : null,
          };

          sendAdd({ data: formattedData });
        }}
      />

      <EditIncidentForm
        incidentId={activeRowId}
        isOpen={activeRowId !== -1}
        isSubmitting={sendEditPending}
        isDeleting={sendDeletePending}
        onClose={() => {
          setActiveRowId(-1);
        }}
        onSubmit={(data) => {
          const formattedData = {
            ...data,
            incidentDate: data.incidentDate ? format(new Date(data.incidentDate), 'yyyy-MM-dd') : null,
            reportedOn: data.reportedOn ? format(new Date(data.reportedOn), 'yyyy-MM-dd') : null,
          };

          sendEdit({ id: activeRowId, data: formattedData });
        }}
        onDelete={() => {
          sendDelete({ id: activeRowId });
        }}
      />
    </>
  );
};

export default SafetyIncidents;
